/**
 * Retrieval cascade — tier merge (design Phase C / plan C6).
 *
 * The unified message retrieval walks three tiers:
 *   Tier 0  live WA store  (freshest — acks, edits, newest)
 *   Tier 1  vault          (IndexedDB, survives WA deletions/eviction)
 *   Tier 2  server backup   (cold, permanent text)
 *
 * `mergeMessageTiers` folds the three ordered lists into one:
 *   - DEDUP by canonical message key. When the same message appears in
 *     more than one tier, the HIGHER tier wins (live > vault > backup) —
 *     it has the freshest state. The winner is kept AS-IS, so a live row
 *     stays unannotated (wire-compat) while a vault/backup row keeps its
 *     `source` annotation.
 *   - SORT by timestamp ascending (the contract row order, plan A.9.1);
 *     stable, so same-second rows keep their tier input order.
 *   - CAP to `limit` after the merge, keeping the NEWEST rows, so a
 *     fallback tier can only ever ADD coverage up to the requested page size.
 *
 * This is intentionally shape-agnostic (pure, no WA/Dexie imports) so the
 * SAME signature can be mirrored on the backend for tier-2 (C6). Default
 * key/timestamp extractors understand both the BtSlimMessage shape
 * (`waMessageKey._serialized`, `t` in seconds) and the raw vault/wire
 * row shapes (`msgId`, `whatsappTimestamp`).
 */

/** Any message-ish object flowing through the cascade. Deliberately
 *  loose — the extractors below reach for whatever identity/time field
 *  the tier happens to carry. */
export interface TieredMessage {
  msgId?: string;
  messageId?: string;
  waMessageKey?: { _serialized?: string } | null;
  id?: { _serialized?: string } | string | null;
  t?: number;
  whatsappTimestamp?: number;
  timestamp?: number;
  source?: 'vault' | 'backup';
  [k: string]: unknown;
}

/** Best-effort canonical identity for a message across tiers. */
export const extractCanonicalKey = (m: TieredMessage): string | undefined => {
  if (typeof m?.msgId === 'string' && m.msgId) return m.msgId;
  const wk = m?.waMessageKey;
  if (wk && typeof wk === 'object' && typeof wk._serialized === 'string' && wk._serialized) {
    return wk._serialized;
  }
  const id = m?.id;
  if (typeof id === 'string' && id) return id;
  if (id && typeof id === 'object' && typeof id._serialized === 'string' && id._serialized) {
    return id._serialized;
  }
  if (typeof m?.messageId === 'string' && m.messageId) return m.messageId;
  return undefined;
};

/** Best-effort sort timestamp. Slim uses `t` (seconds); vault uses
 *  `whatsappTimestamp` (ms). We don't unit-normalize here — every tier
 *  in a given merge is mapped to the same unit by its producer, so a
 *  plain numeric compare orders them correctly. */
export const extractSortT = (m: TieredMessage): number => {
  if (typeof m?.t === 'number') return m.t;
  if (typeof m?.whatsappTimestamp === 'number') return m.whatsappTimestamp;
  if (typeof m?.timestamp === 'number') return m.timestamp;
  return 0;
};

export interface MergeTiersOptions<M> {
  /** Canonical-key extractor. Defaults to {@link extractCanonicalKey}. */
  getKey?: (m: M) => string | undefined;
  /** Sort-timestamp extractor. Defaults to {@link extractSortT}. */
  getT?: (m: M) => number;
  /** Cap the merged result to this many NEWEST rows (after sort). */
  cap?: number;
}

/**
 * Merge live / vault / backup tiers, dedup by canonical key with
 * live > vault > backup precedence, sort ascending (stable), cap to the newest.
 *
 * Rows WITHOUT a resolvable canonical key are always kept (they can't be
 * deduped — dropping them would silently lose data), still sorted+capped.
 */
export function mergeMessageTiers<M extends TieredMessage>(
  live: readonly M[] = [],
  vault: readonly M[] = [],
  backup: readonly M[] = [],
  opts: MergeTiersOptions<M> = {},
): M[] {
  const getKey = opts.getKey ?? (extractCanonicalKey as (m: M) => string | undefined);
  const getT = opts.getT ?? (extractSortT as (m: M) => number);

  const seen = new Set<string>();
  const out: M[] = [];

  // Highest-precedence tier first — the first row seen for a key wins.
  for (const tier of [live, vault, backup]) {
    for (const m of tier) {
      const key = getKey(m);
      if (key === undefined) {
        out.push(m);
        continue;
      }
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(m);
    }
  }

  out.sort((a, b) => getT(a) - getT(b));

  if (typeof opts.cap === 'number' && opts.cap >= 0 && out.length > opts.cap) {
    return out.slice(out.length - opts.cap);
  }
  return out;
}
